// client/src/pages/admin/CustomerDetailPage.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert, Tabs, Tab, Table, Badge } from 'react-bootstrap';
import { FaArrowLeft, FaPlus, FaCar, FaGift, FaTrash } from 'react-icons/fa';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import SettingsContext from '../../context/SettingsContext';
import { hasPermission } from '../../utils/permissions';
import AdjustPointsModal from '../../components/modals/AdjustPointsModal';
import { formatShortDateTime, formatTime, formatDateTime } from '../../utils/format';
import EditCustomerModal from '../../components/modals/EditCustomerModal';
import RegisterVehicleModal from '../../components/modals/RegisterVehicleModal';
import LinkVehicleModal from '../../components/modals/LinkVehicleModal';

const CustomerDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const { settings } = useContext(SettingsContext);

    const [customerData, setCustomerData] = useState(null);
    const [carModels, setCarModels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [showAdjustPointsModal, setShowAdjustPointsModal] = useState(false);
    const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
    const [showRegisterVehicleModal, setShowRegisterVehicleModal] = useState(false);
    const [showLinkVehicleModal, setShowLinkVehicleModal] = useState(false);

    const fetchData = useCallback(async () => {
        if (!user) return;
        if (!customerData) setIsLoading(true);
        try {
            const [customerRes, carModelsRes] = await Promise.all([
                api.get(`/customers/${id}`),
                api.get('/carmodels')
            ]);
            setCustomerData(customerRes.data.data);
            setCarModels(carModelsRes.data.data);
        } catch (err) {
            setError('고객 정보를 불러오는 데 실패했습니다.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [id, user, customerData]);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, user]);

    const handleDeleteCustomer = async () => {
        if (!window.confirm(`정말 "${customerData?.customer?.customerName}" 고객을 삭제하시겠습니까?\n연결된 차량은 유지되며, 고객 정보만 숨김 처리됩니다.`)) return;
        try {
            await api.delete(`/customers/${id}`);
            navigate('/admin/customers');
        } catch (err) {
            alert('고객 삭제에 실패했습니다.');
            console.error(err);
        }
    };

    if (isLoading) {
        return <Container className="text-center mt-5"><Spinner animation="border" /></Container>;
    }

    if (error) {
        return <Container className="mt-5"><Alert variant="danger">{error}</Alert></Container>;
    }

    if (!customerData) {
        return null;
    }

    const { customer, vehicles, serviceHistory, pointHistory } = customerData;

    return (
        <>
            <Button variant="light" className="mb-3" onClick={() => navigate('/admin/customers')}>
                <FaArrowLeft className="me-2" />
                고객 목록으로
            </Button>

            <Card className="mb-4">
                <Card.Header as="h4" className="d-flex justify-content-between align-items-center">
                    {customer.customerName}
                    <Badge bg="primary" pill style={{ fontSize: '1rem' }}>
                        {customer.points.toLocaleString()} P
                    </Badge>
                </Card.Header>
                <Card.Body>
                    <p><strong>연락처:</strong> {customer.phoneNumber || '미등록'}</p>
                    <p><strong>코멘트:</strong> {customer.comment || '-'}</p>
                    {hasPermission(user?.role, 'customer:edit') && (
                        <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => setShowEditCustomerModal(true)}>정보 수정</Button>
                    )}
                    {hasPermission(user?.role, 'customer:adjustPoints') && (
                        <Button variant="outline-success" size="sm" className="me-2" onClick={() => setShowAdjustPointsModal(true)}>
                            <FaGift className="me-1" /> 포인트 조정
                        </Button>
                    )}
                    {hasPermission(user?.role, 'customer:edit') && (
                        <Button variant="outline-danger" size="sm" onClick={handleDeleteCustomer}>
                            <FaTrash className="me-1" /> 고객 삭제
                        </Button>
                    )}
                </Card.Body>
            </Card>

            <Tabs defaultActiveKey="vehicles" id="customer-detail-tabs" className="mb-3">
                <Tab eventKey="vehicles" title={`차량 목록 (${vehicles.length})`}>
                    <Card>
                        <Card.Header className="d-flex justify-content-end">
                            <Button variant="success" size="sm" className="me-2" onClick={() => setShowRegisterVehicleModal(true)}><FaCar className="me-1" /> 신규 차량 등록</Button>
                            <Button variant="info" size="sm" onClick={() => setShowLinkVehicleModal(true)}><FaPlus className="me-1" /> 기존 차량 연결</Button>
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th>차량번호</th>
                                        <th>차종</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vehicles.length > 0 ? vehicles.map(v => (
                                        <tr key={v.id}>
                                            <td>{v.plateNumber}</td>
                                            <td>{v.manufacturer || v.modelName ? `${v.manufacturer || ''} ${v.modelName || ''}`.trim() : <span className="text-muted">차종 미지정</span>}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="2" className="text-center">연결된 차량이 없습니다.</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
                <Tab eventKey="serviceHistory" title={`통합 서비스 내역 (${serviceHistory.length})`}>
                    <Card>
                        <Card.Body>
                            <Table striped bordered hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th>날짜/시간</th>
                                        <th>차량번호</th>
                                        <th>서비스 내용</th>
                                        <th>베이/작업자</th>
                                        <th className="text-end">결제금액</th>
                                        <th>메모</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {serviceHistory.length > 0 ? serviceHistory.map(r => (
                                        <tr key={r.id}>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {formatShortDateTime(r.startTime || r.created_at)}{r.endTime ? `~${formatTime(r.endTime)}` : ''}
                                                {r.source && <Badge bg={r.source === 'walk-in' ? 'secondary' : 'info'} className="ms-1 fw-normal" style={{ fontSize: '0.6rem' }}>{r.source === 'walk-in' ? '현장' : r.source}</Badge>}
                                            </td>
                                            <td>{vehicles.find(v => v.id === r.vehicleId)?.plateNumber}</td>
                                            <td>{r.menuName}{r.optionsText ? ` + ${r.optionsText}` : ''}</td>
                                            <td>
                                                {r.bayNumber ? `${r.bayNumber}번` : '-'}
                                                {r.staffName && <span className="ms-1 text-muted">/ {r.staffName}</span>}
                                            </td>
                                            <td className="text-end">{parseInt(r.finalPrice || 0).toLocaleString()}원 <small className="text-muted">({r.paymentMethod})</small></td>
                                            <td className="small text-muted" style={{ maxWidth: 200 }}>
                                                {r.reservationMemo && <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={`예약메모: ${r.reservationMemo}`}>📋 {r.reservationMemo}</div>}
                                                {r.comment && <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={`작업메모: ${r.comment}`}>🔧 {r.comment}</div>}
                                                {!r.reservationMemo && !r.comment && '-'}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="6" className="text-center">서비스 내역이 없습니다.</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
                <Tab eventKey="pointHistory" title={`포인트 내역 (${pointHistory.length})`}>
                    <Card>
                        <Card.Body>
                            <Table striped bordered hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th>날짜</th>
                                        <th>내용</th>
                                        <th className="text-end">변동</th>
                                        <th className="text-end">잔액</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pointHistory.length > 0 ? pointHistory.map(h => (
                                        <tr key={h.id}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(h.created_at)}</td>
                                            <td>{h.reason}</td>
                                            <td className={`text-end ${h.changeAmount >= 0 ? 'text-success' : 'text-danger'}`}>
                                                {h.changeAmount >= 0 ? '+' : ''}{h.changeAmount.toLocaleString()}
                                            </td>
                                            <td className="text-end">{h.newBalance.toLocaleString()} P</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="4" className="text-center">포인트 변동 내역이 없습니다.</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>

            <AdjustPointsModal 
                show={showAdjustPointsModal}
                onHide={() => setShowAdjustPointsModal(false)}
                customerId={id}
                onSuccess={fetchData}
            />

            {customer && (
                <EditCustomerModal
                    show={showEditCustomerModal}
                    onHide={() => setShowEditCustomerModal(false)}
                    customer={customer}
                    onSuccess={fetchData}
                />
            )}

            <RegisterVehicleModal
                show={showRegisterVehicleModal}
                onHide={() => setShowRegisterVehicleModal(false)}
                customerId={id}
                isCarModelRequired={settings.is_car_model_required}
                carModels={carModels}
                onSuccess={fetchData}
            />

            <LinkVehicleModal
                show={showLinkVehicleModal}
                onHide={() => setShowLinkVehicleModal(false)}
                customerId={id}
                onSuccess={fetchData}
            />
        </>
    );
};

export default CustomerDetailPage;
